import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Container,
  Fade,
  Slide,
  Chip,
  IconButton,
  Paper,
  LinearProgress,
} from '@mui/material';
import {
  TrendingUp,
  Security,
  Analytics,
  LocationOn,
  Settings,
  PlayArrow,
  Pause,
  NavigateBefore,
  NavigateNext,
  Dashboard,
  LocalGasStation,
  Speed,
  Shield,
  Warning,
  CheckCircle,
} from '@mui/icons-material';
import { FuelMonitoringThemeProvider } from '../../theme/ThemeProvider';

interface Slide {
  id: number;
  title: string;
  subtitle?: string;
  content: React.ReactNode;
  background?: string;
}

const Presentation: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right'>('right');

  const slides: Slide[] = [
    {
      id: 1,
      title: 'Fuel Monitoring System',
      subtitle: 'Real-time Fuel Infrastructure Management',
      content: (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, mb: 4, flexWrap: 'wrap' }}>
            <Box
              component="img"
              src="/PetroTag360none.png"
              alt="PetroTag360"
              sx={{
                height: { xs: 80, md: 120 },
                maxWidth: '100%',
                filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1))'
              }}
            />
            <Box
              component="img"
              src="/raec_logo.png"
              alt="RAEC"
              sx={{
                height: { xs: 60, md: 100 },
                maxWidth: '100%',
                filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1))'
              }}
            />
          </Box>
          {/* Hero Image - Conceptual illustration of IoT fuel monitoring */}
          <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
            <Box sx={{
              width: { xs: 300, md: 500 },
              height: { xs: 200, md: 300 },
              background: 'linear-gradient(135deg, rgba(0, 166, 81, 0.1) 0%, rgba(255, 140, 0, 0.1) 100%)',
              borderRadius: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px dashed rgba(0, 166, 81, 0.3)',
              mb: 4
            }}>
              <Box sx={{ textAlign: 'center' }}>
                <LocalGasStation sx={{ fontSize: 60, color: '#00A651', mb: 1 }} />
                <Typography variant="h6" sx={{ color: '#00A651', fontWeight: 700 }}>
                  Smart Fuel Infrastructure
                </Typography>
                <Typography variant="body2" sx={{ color: '#1a365d', fontWeight: 600 }}>
                  IoT-Enabled Monitoring & Analytics
                </Typography>
              </Box>
            </Box>
          </Box>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, color: '#00A651' }}>
            PetroTag360 Smart Fuel Management
          </Typography>
          <Typography variant="h6" sx={{ color: '#1a365d', maxWidth: 700, mx: 'auto', fontWeight: 600 }}>
            Monitor, analyze, and secure your fuel infrastructure with cutting-edge IoT technology
            and real-time analytics for optimal operational efficiency.
          </Typography>
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Chip label="Real-time Monitoring" color="primary" variant="outlined" />
            <Chip label="IoT Integration" color="success" variant="outlined" />
            <Chip label="Advanced Analytics" color="warning" variant="outlined" />
            <Chip label="Security Alerts" color="error" variant="outlined" />
          </Box>
        </Box>
      ),
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    },
    {
      id: 2,
      title: 'Business Need & Market Challenge',
      subtitle: 'Why Fuel Monitoring is Critical for Modern Operations',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      content: (
        <Grid container spacing={4} sx={{ py: 2 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 4, height: '100%', background: 'linear-gradient(45deg, #ff6b6b 0%, #ee5a24 100%)', color: 'white' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                🚨 Current Industry Challenges
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                  <Typography variant="h4" sx={{ color: '#FFD700' }}>25%</Typography>
                  <Box>
                    <Typography variant="body1" fontWeight={600}>Annual fuel theft losses</Typography>
                    <Typography variant="body2">Across industrial operations</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                  <Typography variant="h4" sx={{ color: '#FFD700' }}>40%</Typography>
                  <Box>
                    <Typography variant="body1" fontWeight={600}>Time wasted on manual monitoring</Typography>
                    <Typography variant="body2">Daily operational inefficiencies</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                  <Typography variant="h4" sx={{ color: '#FFD700' }}>60%</Typography>
                  <Box>
                    <Typography variant="body1" fontWeight={600}>Late leak detection incidents</Typography>
                    <Typography variant="body2">Leading to environmental damage</Typography>
                  </Box>
                </Box>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 4, height: '100%', background: 'linear-gradient(45deg, #00A651 0%, #00d4aa 100%)', color: 'white' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                💡 Our Solution Impact
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                  <CheckCircle sx={{ fontSize: 32, color: '#FFD700' }} />
                  <Box>
                    <Typography variant="body1" fontWeight={600}>Real-time Theft Prevention</Typography>
                    <Typography variant="body2">Instant alerts for unauthorized access</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                  <CheckCircle sx={{ fontSize: 32, color: '#FFD700' }} />
                  <Box>
                    <Typography variant="body1" fontWeight={600}>Automated Monitoring</Typography>
                    <Typography variant="body2">99.9% accuracy with IoT sensors</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                  <CheckCircle sx={{ fontSize: 32, color: '#FFD700' }} />
                  <Box>
                    <Typography variant="body1" fontWeight={600}>Early Leak Detection</Typography>
                    <Typography variant="body2">Prevent environmental incidents</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                  <CheckCircle sx={{ fontSize: 32, color: '#FFD700' }} />
                  <Box>
                    <Typography variant="body1" fontWeight={600}>Cost Optimization</Typography>
                    <Typography variant="body2">15-25% reduction in fuel costs</Typography>
                  </Box>
                </Box>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 3, background: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(10px)' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1a365d', textAlign: 'center', textShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}>
                Business Case: Return on Investment Within 6-12 Months
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#FF8C00' }}>$50K+</Typography>
                    <Typography variant="body2" sx={{ color: '#1a365d', fontWeight: 600 }}>Annual savings per site</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#FF8C00' }}>80%</Typography>
                    <Typography variant="body2" sx={{ color: '#1a365d', fontWeight: 600 }}>Reduction in manual tasks</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#FF8C00' }}>99.9%</Typography>
                    <Typography variant="body2" sx={{ color: '#1a365d', fontWeight: 600 }}>System uptime guarantee</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>
      ),
    },
    {
      id: 3,
      title: 'Core Features & Capabilities',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      content: (
        <Grid container spacing={3} sx={{ py: 2 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', background: 'linear-gradient(45deg, #00A651 0%, #00d4aa 100%)' }}>
              <CardContent sx={{ color: 'white', textAlign: 'center', py: 4 }}>
                <Dashboard sx={{ fontSize: 60, mb: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                  Real-time Dashboard
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Live monitoring of fuel levels, consumption rates, and system status across all sites
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                  <Typography variant="body2">• 15-minute data refresh intervals</Typography>
                  <Typography variant="body2">• Multi-site centralized control</Typography>
                  <Typography variant="body2">• Mobile-responsive interface</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', background: 'linear-gradient(45deg, #FF8C00 0%, #ffa726 100%)' }}>
              <CardContent sx={{ color: 'white', textAlign: 'center', py: 4 }}>
                <Analytics sx={{ fontSize: 60, mb: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                  Advanced Analytics
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Predictive analytics, consumption patterns, and optimization recommendations
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                  <Typography variant="body2">• Consumption forecasting</Typography>
                  <Typography variant="body2">• Cost optimization insights</Typography>
                  <Typography variant="body2">• Automated reporting</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)' }}>
              <CardContent sx={{ color: 'white', textAlign: 'center', py: 4 }}>
                <Security sx={{ fontSize: 60, mb: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                  Security & Alerts
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Theft detection, leak monitoring, and instant alert notifications
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                  <Typography variant="body2">• Real-time theft detection</Typography>
                  <Typography variant="body2">• Environmental safety monitoring</Typography>
                  <Typography variant="body2">• Push notification alerts</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', background: 'linear-gradient(45deg, #f093fb 0%, #f5576c 100%)' }}>
              <CardContent sx={{ color: 'white', textAlign: 'center', py: 4 }}>
                <Settings sx={{ fontSize: 60, mb: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                  Role-Based Access
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multi-user support with comprehensive role-based permissions and audit trails
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                  <Typography variant="body2">• Admin, Manager, Operator, Auditor roles</Typography>
                  <Typography variant="body2">• Customizable permissions</Typography>
                  <Typography variant="body2">• Complete audit trail</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ),
    },
    {
      id: 4,
      title: 'Real-time Monitoring Dashboard',
      background: '#ffffff',
      content: (
        <Box sx={{ py: 2 }}>
          <Typography variant="h6" sx={{ mb: 3, color: '#1a365d', textAlign: 'center', fontWeight: 700 }}>
            Live Command Center for Complete Fuel Infrastructure Management
          </Typography>

          {/* Dashboard Concept Visual */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <Box sx={{
              width: { xs: 280, md: 400 },
              height: { xs: 180, md: 250 },
              background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.1) 0%, rgba(0, 166, 81, 0.1) 100%)',
              borderRadius: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid rgba(25, 118, 210, 0.2)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <Box sx={{ textAlign: 'center', zIndex: 2 }}>
                <Dashboard sx={{ fontSize: 50, color: '#1976d2', mb: 1 }} />
                <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 700, mb: 1 }}>
                  Real-time Dashboard
                </Typography>
                <Typography variant="body2" sx={{ color: '#1a365d', fontWeight: 600 }}>
                  Live Data • Analytics • Alerts
                </Typography>
              </Box>
              {/* Decorative elements */}
              <Box sx={{
                position: 'absolute',
                top: 20,
                right: 20,
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: 'rgba(76, 175, 80, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <CheckCircle sx={{ fontSize: 20, color: '#4caf50' }} />
              </Box>
              <Box sx={{
                position: 'absolute',
                bottom: 20,
                left: 20,
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: 'rgba(255, 152, 0, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <TrendingUp sx={{ fontSize: 20, color: '#ff9800' }} />
              </Box>
            </Box>
          </Box>

          {/* Top Row - Main Metrics */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={3}>
              <Paper sx={{ p: 3, textAlign: 'center', background: 'linear-gradient(45deg, #00A651 0%, #00d4aa 100%)', color: 'white', height: '100%' }}>
                <Speed sx={{ fontSize: 48, mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                  2,450L
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                  Current Fuel Level
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={78}
                  sx={{ height: 8, borderRadius: 4, backgroundColor: 'rgba(255,255,255,0.3)' }}
                />
                <Typography variant="caption" sx={{ mt: 1, display: 'block', opacity: 0.9 }}>
                  78% Capacity | 3,150L Total
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={3}>
              <Paper sx={{ p: 3, textAlign: 'center', background: 'linear-gradient(45deg, #FF8C00 0%, #ffa726 100%)', color: 'white', height: '100%' }}>
                <TrendingUp sx={{ fontSize: 48, mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                  85.2%
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                  Efficiency Rating
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={85}
                  sx={{ height: 8, borderRadius: 4, backgroundColor: 'rgba(255,255,255,0.3)' }}
                />
                <Typography variant="caption" sx={{ mt: 1, display: 'block', opacity: 0.9 }}>
                  +12% vs Last Month
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={3}>
              <Paper sx={{ p: 3, textAlign: 'center', background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)', color: 'white', height: '100%' }}>
                <Security sx={{ fontSize: 48, mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                  24/7
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                  Security Monitoring
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <Chip label="SECURE" sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 'bold' }} />
                </Box>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  No Threats Detected
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={3}>
              <Paper sx={{ p: 3, textAlign: 'center', background: 'linear-gradient(45deg, #4caf50 0%, #81c784 100%)', color: 'white', height: '100%' }}>
                <CheckCircle sx={{ fontSize: 48, mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                  12
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                  Active Sites
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <Chip label="ALL ONLINE" sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 'bold' }} />
                </Box>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  99.9% Uptime This Month
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Secondary Metrics Row */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, background: '#f8f9ff', border: '2px solid #e3f2fd' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2' }}>
                    Daily Consumption
                  </Typography>
                  <LocationOn sx={{ color: '#1976d2' }} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#1976d2', mb: 1 }}>
                  1,250L
                </Typography>
                <Typography variant="body1" sx={{ color: '#1a365d', fontWeight: 600, mb: 2 }}>
                  Target: 1,920L (65% consumed)
                </Typography>
                <LinearProgress variant="determinate" value={65} sx={{ height: 10, borderRadius: 5, mb: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" sx={{ color: '#1a365d', fontWeight: 600 }}>Morning Peak: 8AM</Typography>
                  <Typography variant="caption" sx={{ color: '#1a365d', fontWeight: 600 }}>Rate: 52L/hr</Typography>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, background: '#fff3e0', border: '2px solid #ffcc80' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#f57c00' }}>
                    Temperature Control
                  </Typography>
                  <Settings sx={{ color: '#f57c00' }} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#f57c00', mb: 1 }}>
                  22°C
                </Typography>
                <Typography variant="body1" sx={{ color: '#1a365d', fontWeight: 600, mb: 2 }}>
                  Optimal Range: 18-25°C
                </Typography>
                <LinearProgress variant="determinate" value={80} color="warning" sx={{ height: 10, borderRadius: 5, mb: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" sx={{ color: '#1a365d', fontWeight: 600 }}>Tank 1: 21°C</Typography>
                  <Typography variant="caption" sx={{ color: '#1a365d', fontWeight: 600 }}>Tank 2: 23°C</Typography>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, background: '#f3e5f5', border: '2px solid #ce93d8' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#7b1fa2' }}>
                    Quality Index
                  </Typography>
                  <Analytics sx={{ color: '#7b1fa2' }} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#7b1fa2', mb: 1 }}>
                  98.5%
                </Typography>
                <Typography variant="body1" sx={{ color: '#1a365d', fontWeight: 600, mb: 2 }}>
                  Water Content: 0.02%
                </Typography>
                <LinearProgress variant="determinate" value={98.5} sx={{ height: 10, borderRadius: 5, mb: 2, '& .MuiLinearProgress-bar': { backgroundColor: '#7b1fa2' } }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" sx={{ color: '#1a365d', fontWeight: 600 }}>Density: Normal</Typography>
                  <Typography variant="caption" sx={{ color: '#1a365d', fontWeight: 600 }}>Last Test: 2hrs ago</Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Alert Status and Quick Actions */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, background: 'linear-gradient(45deg, #e8f5e8 0%, #c8e6c9 100%)', border: '2px solid #4caf50' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#2e7d32', mb: 3 }}>
                  System Health & Alerts
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} />
                      <Typography variant="body1" sx={{ fontWeight: 600, color: '#1a365d' }}>All Systems Operational</Typography>
                    </Box>
                    <Chip label="NORMAL" color="success" size="small" sx={{ fontWeight: 'bold' }} />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} />
                      <Typography variant="body1" sx={{ fontWeight: 600, color: '#1a365d' }}>No Security Breaches</Typography>
                    </Box>
                    <Chip label="SECURE" color="success" size="small" sx={{ fontWeight: 'bold' }} />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Warning sx={{ color: '#ff9800', fontSize: 20 }} />
                      <Typography variant="body1" sx={{ fontWeight: 600, color: '#1a365d' }}>Routine Maintenance Due</Typography>
                    </Box>
                    <Chip label="SCHEDULED" color="warning" size="small" sx={{ fontWeight: 'bold' }} />
                  </Box>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, background: 'linear-gradient(45deg, #f0f4ff 0%, #e1f5fe 100%)', border: '2px solid #2196f3' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2', mb: 3 }}>
                  Live Data Streams
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#1a365d' }}>Data Refresh Rate:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 800, color: '#1976d2' }}>Every 15 seconds</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#1a365d' }}>Sensors Active:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 800, color: '#1976d2' }}>48/48 Online</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#1a365d' }}>Last Sync:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 800, color: '#1976d2' }}>2 seconds ago</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#1a365d' }}>Network Status:</Typography>
                    <Chip label="EXCELLENT" color="primary" size="small" sx={{ fontWeight: 'bold' }} />
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      ),
    },
    {
      id: 5,
      title: 'Advanced Analytics & Insights',
      background: 'linear-gradient(135deg, #fafbfc 0%, #e8f4fd 100%)',
      content: (
        <Grid container spacing={4} sx={{ py: 2 }}>
          <Typography variant="h6" sx={{ mb: 3, color: '#1a365d', textAlign: 'center', fontWeight: 700 }}>
            AI-Powered Intelligence for Optimal Fuel Management Decisions
          </Typography>

          {/* Top Row - Main Analytics Cards */}
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 4, height: '100%', background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 2 }}>
                <TrendingUp sx={{ fontSize: 32 }} />
                Consumption Analytics
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>Daily Consumption</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>1,250L</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={65} sx={{ height: 12, borderRadius: 6, mb: 1, backgroundColor: 'rgba(255,255,255,0.3)' }} />
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>Target: 1,920L (65% of daily target achieved)</Typography>
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>Weekly Efficiency Trend</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#FFD700' }}>+12%</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={78} sx={{ height: 12, borderRadius: 6, mb: 1, backgroundColor: 'rgba(255,255,255,0.3)', '& .MuiLinearProgress-bar': { backgroundColor: '#4caf50' } }} />
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>Significant improvement over last week</Typography>
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>Cost Optimization</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#FFD700' }}>$2,340</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={85} sx={{ height: 12, borderRadius: 6, mb: 1, backgroundColor: 'rgba(255,255,255,0.3)', '& .MuiLinearProgress-bar': { backgroundColor: '#FF8C00' } }} />
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>Monthly savings vs manual monitoring</Typography>
                </Box>
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ p: 4, height: '100%', background: 'linear-gradient(45deg, #00A651 0%, #00d4aa 100%)', color: 'white' }}>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Analytics sx={{ fontSize: 32 }} />
                Predictive Intelligence
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box sx={{ p: 3, background: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <TrendingUp sx={{ fontSize: 24, color: '#FFD700' }} />
                    <Typography variant="h6" fontWeight={700}>Optimal Refill Schedule</Typography>
                  </Box>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>Next Refill: Tuesday, 3:00 PM</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    AI recommends refill in 3 days based on consumption patterns and inventory levels
                  </Typography>
                </Box>
                <Box sx={{ p: 3, background: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Warning sx={{ fontSize: 24, color: '#FFD700' }} />
                    <Typography variant="h6" fontWeight={700}>Smart Alerts</Typography>
                  </Box>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>Maintenance Due: Tank #3</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Sensor calibration recommended within 5 days to maintain 99.9% accuracy
                  </Typography>
                </Box>
                <Box sx={{ p: 3, background: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Speed sx={{ fontSize: 24, color: '#FFD700' }} />
                    <Typography variant="h6" fontWeight={700}>Performance Forecast</Typography>
                  </Box>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>Peak Usage: 8:00 AM - 10:00 AM</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Predicted 15% increase in consumption during morning operations
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>

          {/* Second Row - Detailed Analytics */}
          <Grid item xs={12} md={4}>
            <Card sx={{ p: 3, height: '100%', background: '#f8f9ff', border: '2px solid #e3f2fd' }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#1976d2', display: 'flex', alignItems: 'center', gap: 2 }}>
                <Dashboard sx={{ fontSize: 24 }} />
                Real-time Metrics
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 700, color: '#1a365d', mb: 1 }}>Flow Rate Analysis</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: '#1976d2', mb: 1 }}>52.3 L/hr</Typography>
                  <Typography variant="body2" sx={{ color: '#1a365d', fontWeight: 600 }}>
                    Current rate | Avg: 45.2 L/hr | Peak: 87.5 L/hr
                  </Typography>
                  <LinearProgress variant="determinate" value={60} sx={{ mt: 2, height: 8, borderRadius: 4 }} />
                </Box>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 700, color: '#1a365d', mb: 1 }}>Variance Analysis</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: '#4caf50', mb: 1 }}>±2.1%</Typography>
                  <Typography variant="body2" sx={{ color: '#1a365d', fontWeight: 600 }}>
                    Within acceptable range (±5%) for optimal operations
                  </Typography>
                  <LinearProgress variant="determinate" value={95} color="success" sx={{ mt: 2, height: 8, borderRadius: 4 }} />
                </Box>
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ p: 3, height: '100%', background: '#fff3e0', border: '2px solid #ffcc80' }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#f57c00', display: 'flex', alignItems: 'center', gap: 2 }}>
                <LocalGasStation sx={{ fontSize: 24 }} />
                Consumption Patterns
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 700, color: '#1a365d', mb: 1 }}>Daily Peak Hours</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: '#f57c00', mb: 1 }}>8-10 AM</Typography>
                  <Typography variant="body2" sx={{ color: '#1a365d', fontWeight: 600 }}>
                    87% of daily consumption occurs during morning operations
                  </Typography>
                  <LinearProgress variant="determinate" value={87} color="warning" sx={{ mt: 2, height: 8, borderRadius: 4 }} />
                </Box>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 700, color: '#1a365d', mb: 1 }}>Weekly Patterns</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: '#f57c00', mb: 1 }}>Mon-Wed</Typography>
                  <Typography variant="body2" sx={{ color: '#1a365d', fontWeight: 600 }}>
                    Highest consumption mid-week, 25% lower on weekends
                  </Typography>
                  <LinearProgress variant="determinate" value={75} color="warning" sx={{ mt: 2, height: 8, borderRadius: 4 }} />
                </Box>
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ p: 3, height: '100%', background: '#f3e5f5', border: '2px solid #ce93d8' }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#7b1fa2', display: 'flex', alignItems: 'center', gap: 2 }}>
                <Security sx={{ fontSize: 24 }} />
                Anomaly Detection
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 700, color: '#1a365d', mb: 1 }}>System Health</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: '#4caf50', mb: 1 }}>Normal</Typography>
                  <Typography variant="body2" sx={{ color: '#1a365d', fontWeight: 600 }}>
                    No anomalies detected in last 30 days
                  </Typography>
                  <LinearProgress variant="determinate" value={100} sx={{ mt: 2, height: 8, borderRadius: 4, '& .MuiLinearProgress-bar': { backgroundColor: '#4caf50' } }} />
                </Box>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 700, color: '#1a365d', mb: 1 }}>Theft Prevention</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: '#4caf50', mb: 1 }}>Active</Typography>
                  <Typography variant="body2" sx={{ color: '#1a365d', fontWeight: 600 }}>
                    AI monitoring detects unauthorized access within 30 seconds
                  </Typography>
                  <LinearProgress variant="determinate" value={100} sx={{ mt: 2, height: 8, borderRadius: 4, '& .MuiLinearProgress-bar': { backgroundColor: '#4caf50' } }} />
                </Box>
              </Box>
            </Card>
          </Grid>

          {/* Third Row - Advanced Insights */}
          <Grid item xs={12}>
            <Card sx={{ p: 4, background: 'linear-gradient(45deg, #FF8C00 0%, #ffa726 100%)', color: 'white' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, textAlign: 'center' }}>
                Machine Learning Insights & Recommendations
              </Typography>
              <Grid container spacing={4}>
                <Grid item xs={12} md={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 800, color: '#FFD700', mb: 1 }}>94.7%</Typography>
                    <Typography variant="h6" fontWeight={700}>Prediction Accuracy</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      ML models achieve 94.7% accuracy in consumption forecasting
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 800, color: '#FFD700', mb: 1 }}>15%</Typography>
                    <Typography variant="h6" fontWeight={700}>Cost Reduction</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Average cost savings through optimization recommendations
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 800, color: '#FFD700', mb: 1 }}>30s</Typography>
                    <Typography variant="h6" fontWeight={700}>Alert Response</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Average time to detect and alert on anomalies
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 800, color: '#FFD700', mb: 1 }}>24/7</Typography>
                    <Typography variant="h6" fontWeight={700}>Continuous Learning</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      AI models continuously adapt to operational patterns
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>
      ),
    },
    {
      id: 6,
      title: 'Security & Quality Monitoring',
      background: '#ffffff',
      content: (
        <Grid container spacing={4} sx={{ py: 2 }}>
          <Typography variant="h6" sx={{ mb: 3, color: '#1a365d', textAlign: 'center', fontWeight: 700 }}>
            Comprehensive Protection & Quality Assurance for Your Fuel Assets
          </Typography>

          {/* Top Row - Main Security & Quality Cards */}
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 4, background: 'linear-gradient(45deg, #ff6b6b 0%, #ee5a24 100%)', color: 'white', height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Shield sx={{ fontSize: 48, mr: 2 }} />
                <Typography variant="h5" fontWeight={800}>
                  Advanced Security Features
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box sx={{ p: 3, background: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" fontWeight={700}>Theft Detection System</Typography>
                    <CheckCircle sx={{ fontSize: 24, color: '#FFD700' }} />
                  </Box>
                  <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>Real-time unauthorized access detection</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Response Time:</Typography>
                    <Typography variant="body2" fontWeight={700}>&lt; 30 seconds</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Detection Accuracy:</Typography>
                    <Typography variant="body2" fontWeight={700}>99.8%</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>False Positive Rate:</Typography>
                    <Typography variant="body2" fontWeight={700}>&lt; 0.1%</Typography>
                  </Box>
                </Box>
                <Box sx={{ p: 3, background: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" fontWeight={700}>Environmental Monitoring</Typography>
                    <CheckCircle sx={{ fontSize: 24, color: '#FFD700' }} />
                  </Box>
                  <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>Leak detection & environmental safety</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Leak Detection:</Typography>
                    <Typography variant="body2" fontWeight={700}>0.1L accuracy</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Pressure Monitoring:</Typography>
                    <Typography variant="body2" fontWeight={700}>±0.5 PSI</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Environmental Compliance:</Typography>
                    <Typography variant="body2" fontWeight={700}>100% Certified</Typography>
                  </Box>
                </Box>
                <Box sx={{ p: 3, background: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" fontWeight={700}>Access Control System</Typography>
                    <CheckCircle sx={{ fontSize: 24, color: '#FFD700' }} />
                  </Box>
                  <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>Multi-factor authentication & audit trails</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Active Users:</Typography>
                    <Typography variant="body2" fontWeight={700}>24 Authorized</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Audit Trail:</Typography>
                    <Typography variant="body2" fontWeight={700}>Complete Logs</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Encryption Level:</Typography>
                    <Typography variant="body2" fontWeight={700}>AES-256</Typography>
                  </Box>
                </Box>
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ p: 4, background: 'linear-gradient(45deg, #4ecdc4 0%, #2ecc71 100%)', color: 'white', height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Analytics sx={{ fontSize: 48, mr: 2 }} />
                <Typography variant="h5" fontWeight={800}>
                  Quality Control & Analysis
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box sx={{ p: 3, background: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                  <Typography variant="h6" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Speed sx={{ fontSize: 24, color: '#FFD700' }} />
                    Fuel Quality Index
                  </Typography>
                  <Typography variant="h4" fontWeight={800} sx={{ mb: 2, color: '#FFD700' }}>98.5%</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Octane Rating:</Typography>
                    <Typography variant="body2" fontWeight={700}>95.2 RON</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Sulfur Content:</Typography>
                    <Typography variant="body2" fontWeight={700}>8.5 ppm</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Density @ 15°C:</Typography>
                    <Typography variant="body2" fontWeight={700}>742 kg/m³</Typography>
                  </Box>
                </Box>
                <Box sx={{ p: 3, background: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                  <Typography variant="h6" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Warning sx={{ fontSize: 24, color: '#FFD700' }} />
                    Contamination Analysis
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body1" fontWeight={600}>Water Content</Typography>
                    <Typography variant="h6" fontWeight={800} sx={{ color: '#FFD700' }}>0.02%</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Acceptable Range:</Typography>
                    <Typography variant="body2" fontWeight={700}>&lt; 0.05%</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Sediment Level:</Typography>
                    <Typography variant="body2" fontWeight={700}>0.001%</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Last Test:</Typography>
                    <Typography variant="body2" fontWeight={700}>2 hours ago</Typography>
                  </Box>
                </Box>
                <Box sx={{ p: 3, background: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                  <Typography variant="h6" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Settings sx={{ fontSize: 24, color: '#FFD700' }} />
                    Environmental Conditions
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body1" fontWeight={600}>Temperature</Typography>
                    <Typography variant="h6" fontWeight={800} sx={{ color: '#FFD700' }}>22°C</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Optimal Range:</Typography>
                    <Typography variant="body2" fontWeight={700}>18-25°C</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Vapor Pressure:</Typography>
                    <Typography variant="body2" fontWeight={700}>65 kPa</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Humidity Level:</Typography>
                    <Typography variant="body2" fontWeight={700}>45% RH</Typography>
                  </Box>
                </Box>
              </Box>
            </Card>
          </Grid>

          {/* Second Row - Alert Systems and Compliance */}
          <Grid item xs={12} md={4}>
            <Card sx={{ p: 3, height: '100%', background: '#f8f9ff', border: '2px solid #e3f2fd' }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#1976d2', display: 'flex', alignItems: 'center', gap: 2 }}>
                <Security sx={{ fontSize: 24 }} />
                Real-time Alert System
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ p: 2, background: '#e8f5e8', borderRadius: 1, border: '1px solid #4caf50' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} />
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#1a365d' }}>All Systems Normal</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                    No security threats detected in last 24 hours
                  </Typography>
                </Box>
                <Box sx={{ p: 2, background: '#fff3e0', borderRadius: 1, border: '1px solid #ff9800' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <Warning sx={{ color: '#ff9800', fontSize: 20 }} />
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#1a365d' }}>Routine Maintenance</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#f57c00', fontWeight: 600 }}>
                    Sensor #A3 calibration due in 48 hours
                  </Typography>
                </Box>
                <Box sx={{ p: 2, background: '#e3f2fd', borderRadius: 1, border: '1px solid #2196f3' }}>
                  <Typography variant="body1" sx={{ fontWeight: 700, color: '#1a365d', mb: 1 }}>Active Monitoring</Typography>
                  <Typography variant="body2" sx={{ color: '#1976d2', fontWeight: 600 }}>
                    48 sensors | 12 sites | 99.9% uptime
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ p: 3, height: '100%', background: '#fff3e0', border: '2px solid #ffcc80' }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#f57c00', display: 'flex', alignItems: 'center', gap: 2 }}>
                <CheckCircle sx={{ fontSize: 24 }} />
                Compliance & Standards
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 700, color: '#1a365d', mb: 1 }}>Industry Standards</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ color: '#1a365d', fontWeight: 600 }}>ISO 14001:</Typography>
                    <Chip label="CERTIFIED" color="success" size="small" sx={{ fontWeight: 'bold' }} />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ color: '#1a365d', fontWeight: 600 }}>API Standards:</Typography>
                    <Chip label="COMPLIANT" color="success" size="small" sx={{ fontWeight: 'bold' }} />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ color: '#1a365d', fontWeight: 600 }}>EPA Regulations:</Typography>
                    <Chip label="APPROVED" color="success" size="small" sx={{ fontWeight: 'bold' }} />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 700, color: '#1a365d', mb: 1 }}>Audit Status</Typography>
                  <Typography variant="body2" sx={{ color: '#1a365d', fontWeight: 600, mb: 1 }}>
                    Last Audit: October 2024
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1a365d', fontWeight: 600, mb: 1 }}>
                    Next Audit: April 2025
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1a365d', fontWeight: 600 }}>
                    Compliance Score: 98.7%
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ p: 3, height: '100%', background: '#f3e5f5', border: '2px solid #ce93d8' }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#7b1fa2', display: 'flex', alignItems: 'center', gap: 2 }}>
                <TrendingUp sx={{ fontSize: 24 }} />
                Quality Trends
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 700, color: '#1a365d', mb: 2 }}>30-Day Quality Trend</Typography>
                  <LinearProgress variant="determinate" value={98.5} sx={{ height: 10, borderRadius: 5, mb: 1, '& .MuiLinearProgress-bar': { backgroundColor: '#7b1fa2' } }} />
                  <Typography variant="body2" sx={{ color: '#1a365d', fontWeight: 600 }}>
                    Average: 98.5% | Target: 95%
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 700, color: '#1a365d', mb: 2 }}>Contamination Events</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: '#4caf50', mb: 1 }}>0</Typography>
                  <Typography variant="body2" sx={{ color: '#1a365d', fontWeight: 600 }}>
                    Zero contamination incidents this month
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 700, color: '#1a365d', mb: 1 }}>Testing Frequency</Typography>
                  <Typography variant="body2" sx={{ color: '#1a365d', fontWeight: 600 }}>
                    Automated: Every 2 hours
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1a365d', fontWeight: 600 }}>
                    Manual: Daily comprehensive
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1a365d', fontWeight: 600 }}>
                    Lab Analysis: Weekly
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>

          {/* Bottom Row - Security Statistics */}
          <Grid item xs={12}>
            <Card sx={{ p: 4, background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, textAlign: 'center' }}>
                Security & Quality Performance Metrics
              </Typography>
              <Grid container spacing={4}>
                <Grid item xs={12} md={2}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 800, color: '#FFD700', mb: 1 }}>0</Typography>
                    <Typography variant="h6" fontWeight={700}>Security Breaches</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>In the last 12 months</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 800, color: '#FFD700', mb: 1 }}>99.8%</Typography>
                    <Typography variant="h6" fontWeight={700}>Detection Accuracy</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Threat identification rate</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 800, color: '#FFD700', mb: 1 }}>98.5%</Typography>
                    <Typography variant="h6" fontWeight={700}>Quality Score</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Average fuel quality index</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 800, color: '#FFD700', mb: 1 }}>24/7</Typography>
                    <Typography variant="h6" fontWeight={700}>Monitoring</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Continuous surveillance</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 800, color: '#FFD700', mb: 1 }}>30s</Typography>
                    <Typography variant="h6" fontWeight={700}>Response Time</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Average alert response</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 800, color: '#FFD700', mb: 1 }}>100%</Typography>
                    <Typography variant="h6" fontWeight={700}>Compliance</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Industry standards adherence</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>
      ),
    },
    {
      id: 7,
      title: 'Benefits & ROI',
      background: 'linear-gradient(135deg, #f8fafb 0%, #e6f3ff 100%)',
      content: (
        <Box sx={{ py: 2 }}>
          <Typography variant="h6" sx={{ mb: 4, color: '#1a365d', textAlign: 'center', fontWeight: 700 }}>
            Comprehensive Return on Investment & Business Value Proposition
          </Typography>

          {/* ROI Summary Row */}
          <Grid container spacing={4} sx={{ mb: 4 }}>
            <Grid item xs={12}>
              <Card sx={{ p: 4, background: 'linear-gradient(45deg, #00A651 0%, #00d4aa 100%)', color: 'white' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, textAlign: 'center' }}>
                  Investment Payback Timeline & Expected Returns
                </Typography>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h2" sx={{ fontWeight: 800, color: '#FFD700', mb: 1 }}>6-12</Typography>
                      <Typography variant="h6" fontWeight={700}>Months Payback</Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Full return on initial investment within first year
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h2" sx={{ fontWeight: 800, color: '#FFD700', mb: 1 }}>$250K+</Typography>
                      <Typography variant="h6" fontWeight={700}>Annual Savings</Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Average yearly cost reduction per facility
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h2" sx={{ fontWeight: 800, color: '#FFD700', mb: 1 }}>300%</Typography>
                      <Typography variant="h6" fontWeight={700}>3-Year ROI</Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Net positive return after implementation costs
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h2" sx={{ fontWeight: 800, color: '#FFD700', mb: 1 }}>98%</Typography>
                      <Typography variant="h6" fontWeight={700}>Customer Satisfaction</Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Client retention and satisfaction rate
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          </Grid>

          {/* Detailed Benefits Row */}
          <Grid container spacing={4} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', height: '100%' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TrendingUp sx={{ fontSize: 32 }} />
                  Direct Cost Savings
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Box sx={{ p: 3, background: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" fontWeight={700}>Theft Prevention Savings</Typography>
                      <Typography variant="h4" fontWeight={800} sx={{ color: '#FFD700' }}>25%</Typography>
                    </Box>
                    <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>Annual fuel theft reduction</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Average facility saves $15,000-$50,000 annually through advanced theft detection and prevention systems
                    </Typography>
                  </Box>
                  <Box sx={{ p: 3, background: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" fontWeight={700}>Operational Efficiency</Typography>
                      <Typography variant="h4" fontWeight={800} sx={{ color: '#FFD700' }}>15%</Typography>
                    </Box>
                    <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>Improved fuel efficiency & optimization</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Smart analytics reduce wastage and optimize consumption patterns, saving 10-20% on fuel costs
                    </Typography>
                  </Box>
                  <Box sx={{ p: 3, background: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" fontWeight={700}>Labor Cost Reduction</Typography>
                      <Typography variant="h4" fontWeight={800} sx={{ color: '#FFD700' }}>80%</Typography>
                    </Box>
                    <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>Automated monitoring eliminates manual checks</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Reduce staff hours spent on fuel monitoring by 6-8 hours daily per facility
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ p: 4, background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white', height: '100%' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Speed sx={{ fontSize: 32 }} />
                  Operational Benefits
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Box sx={{ p: 3, background: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" fontWeight={700}>Issue Detection Speed</Typography>
                      <Typography variant="h4" fontWeight={800} sx={{ color: '#FFD700' }}>90%</Typography>
                    </Box>
                    <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>Faster problem identification & resolution</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Real-time alerts detect issues within 30 seconds vs 6-12 hours with manual monitoring
                    </Typography>
                  </Box>
                  <Box sx={{ p: 3, background: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" fontWeight={700}>System Reliability</Typography>
                      <Typography variant="h4" fontWeight={800} sx={{ color: '#FFD700' }}>99.9%</Typography>
                    </Box>
                    <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>Guaranteed system uptime & availability</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Enterprise-grade infrastructure ensures continuous monitoring and minimal downtime
                    </Typography>
                  </Box>
                  <Box sx={{ p: 3, background: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" fontWeight={700}>Compliance Adherence</Typography>
                      <Typography variant="h4" fontWeight={800} sx={{ color: '#FFD700' }}>100%</Typography>
                    </Box>
                    <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>Environmental & safety regulation compliance</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Automated reporting and monitoring ensure full compliance with industry regulations
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>
          </Grid>

          {/* Detailed ROI Breakdown Row */}
          <Grid container spacing={4} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: '100%', background: '#f8f9ff', border: '2px solid #e3f2fd' }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#1976d2', display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Analytics sx={{ fontSize: 24 }} />
                  Financial Impact Analysis
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#1a365d', mb: 1 }}>Initial Investment</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#1976d2', mb: 1 }}>$85,000</Typography>
                    <Typography variant="body2" sx={{ color: '#1a365d', fontWeight: 600 }}>
                      Complete system setup including hardware, software, installation, and training
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#1a365d', mb: 1 }}>Year 1 Savings</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#4caf50', mb: 1 }}>$127,000</Typography>
                    <Typography variant="body2" sx={{ color: '#1a365d', fontWeight: 600 }}>
                      Immediate cost reductions from theft prevention and operational efficiency
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#1a365d', mb: 1 }}>Net Benefit Year 1</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#00A651', mb: 1 }}>$42,000</Typography>
                    <Typography variant="body2" sx={{ color: '#1a365d', fontWeight: 600 }}>
                      Positive return within first 12 months of operation
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: '100%', background: '#fff3e0', border: '2px solid #ffcc80' }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#f57c00', display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Shield sx={{ fontSize: 24 }} />
                  Risk Mitigation Value
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#1a365d', mb: 1 }}>Insurance Premium Reduction</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#f57c00', mb: 1 }}>12-18%</Typography>
                    <Typography variant="body2" sx={{ color: '#1a365d', fontWeight: 600 }}>
                      Annual savings on insurance due to enhanced security measures
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#1a365d', mb: 1 }}>Environmental Liability</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#4caf50', mb: 1 }}>95%</Typography>
                    <Typography variant="body2" sx={{ color: '#1a365d', fontWeight: 600 }}>
                      Reduction in potential environmental incidents and associated costs
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#1a365d', mb: 1 }}>Regulatory Compliance</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#7b1fa2', mb: 1 }}>$50K+</Typography>
                    <Typography variant="body2" sx={{ color: '#1a365d', fontWeight: 600 }}>
                      Avoided fines and penalties through automated compliance monitoring
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: '100%', background: '#f3e5f5', border: '2px solid #ce93d8' }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#7b1fa2', display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CheckCircle sx={{ fontSize: 24 }} />
                  Competitive Advantages
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#1a365d', mb: 1 }}>Market Differentiation</Typography>
                    <Typography variant="body2" sx={{ color: '#1a365d', fontWeight: 600, mb: 2 }}>
                      Advanced IoT capabilities set you apart from competitors using manual systems
                    </Typography>
                    <Chip label="COMPETITIVE EDGE" color="primary" size="small" sx={{ fontWeight: 'bold' }} />
                  </Box>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#1a365d', mb: 1 }}>Customer Confidence</Typography>
                    <Typography variant="body2" sx={{ color: '#1a365d', fontWeight: 600, mb: 2 }}>
                      Real-time transparency and reporting build trust with clients and stakeholders
                    </Typography>
                    <Chip label="TRUST BUILDING" color="success" size="small" sx={{ fontWeight: 'bold' }} />
                  </Box>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#1a365d', mb: 1 }}>Scalability</Typography>
                    <Typography variant="body2" sx={{ color: '#1a365d', fontWeight: 600, mb: 2 }}>
                      System grows with your business, supporting expansion without major reinvestment
                    </Typography>
                    <Chip label="FUTURE-READY" color="warning" size="small" sx={{ fontWeight: 'bold' }} />
                  </Box>
                </Box>
              </Card>
            </Grid>
          </Grid>

          {/* Implementation Value */}
          <Grid item xs={12}>
            <Card sx={{ p: 4, background: 'linear-gradient(45deg, #FF8C00 0%, #ffa726 100%)', color: 'white' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, textAlign: 'center' }}>
                Total Value Proposition & Implementation Benefits
              </Typography>
              <Grid container spacing={4}>
                <Grid item xs={12} md={2}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 800, color: '#FFD700', mb: 1 }}>$1.2M</Typography>
                    <Typography variant="h6" fontWeight={700}>5-Year Savings</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Cumulative cost benefits</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 800, color: '#FFD700', mb: 1 }}>45%</Typography>
                    <Typography variant="h6" fontWeight={700}>Productivity Gain</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Operational efficiency improvement</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 800, color: '#FFD700', mb: 1 }}>Zero</Typography>
                    <Typography variant="h6" fontWeight={700}>Theft Incidents</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>With proper implementation</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 800, color: '#FFD700', mb: 1 }}>24/7</Typography>
                    <Typography variant="h6" fontWeight={700}>Peace of Mind</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Continuous protection</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 800, color: '#FFD700', mb: 1 }}>100%</Typography>
                    <Typography variant="h6" fontWeight={700}>Transparency</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Complete operational visibility</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 800, color: '#FFD700', mb: 1 }}>Future</Typography>
                    <Typography variant="h6" fontWeight={700}>Ready</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Scalable IoT infrastructure</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Box>
      ),
    },
    {
      id: 8,
      title: 'IoT Architecture & Technical Overview',
      background: '#ffffff',
      content: (
        <Grid container spacing={4} sx={{ py: 2 }}>
          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src="/Fuel_monitoring_IOT.png"
              alt="IoT Architecture"
              sx={{
                width: '100%',
                height: 'auto',
                borderRadius: 2,
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
                background: 'white',
                p: 1,
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Card sx={{ p: 3, background: 'linear-gradient(45deg, #00A651 0%, #00d4aa 100%)', color: 'white' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  IoT Sensor Network
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2">• Ultrasonic level sensors</Typography>
                  <Typography variant="body2">• Temperature monitoring</Typography>
                  <Typography variant="body2">• Flow rate sensors</Typography>
                  <Typography variant="body2">• Pressure sensors</Typography>
                  <Typography variant="body2">• Water detection sensors</Typography>
                </Box>
              </Card>
              <Card sx={{ p: 3, background: 'linear-gradient(45deg, #FF8C00 0%, #ffa726 100%)', color: 'white' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  Communication Protocols
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2">• LoRaWAN for long-range</Typography>
                  <Typography variant="body2">• 4G/5G cellular connectivity</Typography>
                  <Typography variant="body2">• WiFi for local networks</Typography>
                  <Typography variant="body2">• Modbus RTU/TCP</Typography>
                  <Typography variant="body2">• MQTT for real-time data</Typography>
                </Box>
              </Card>
            </Box>
          </Grid>
        </Grid>
      ),
    },
    {
      id: 9,
      title: 'System Architecture & Data Flow',
      background: 'linear-gradient(135deg, #fafcfd 0%, #f0f8ff 100%)',
      content: (
        <Grid container spacing={4} sx={{ py: 2 }}>
          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src="/Fuel_monitoring.png"
              alt="System Architecture"
              sx={{
                width: '100%',
                height: 'auto',
                borderRadius: 2,
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
                background: 'white',
                p: 1,
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Card sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#00A651' }}>
                  Data Collection Layer
                </Typography>
                <Typography variant="body2" sx={{ color: '#1a365d', fontWeight: 600 }}>
                  Real-time data collection from distributed IoT sensors with 99.9% uptime reliability
                </Typography>
              </Card>
              <Card sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#FF8C00' }}>
                  Processing & Analytics
                </Typography>
                <Typography variant="body2" sx={{ color: '#1a365d', fontWeight: 600 }}>
                  Edge computing and cloud-based analytics for predictive maintenance and optimization
                </Typography>
              </Card>
              <Card sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#667eea' }}>
                  User Interface
                </Typography>
                <Typography variant="body2" sx={{ color: '#1a365d', fontWeight: 600 }}>
                  Responsive web dashboard with mobile apps for real-time monitoring and control
                </Typography>
              </Card>
            </Box>
          </Grid>
        </Grid>
      ),
    },
    {
      id: 10,
      title: 'Technical Specifications',
      background: '#ffffff',
      content: (
        <Grid container spacing={3} sx={{ py: 2 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#00A651' }}>
                Hardware Specifications
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" fontWeight={600}>Sensor Accuracy:</Typography>
                  <Chip label="±0.25%" size="small" color="success" />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" fontWeight={600}>Operating Temperature:</Typography>
                  <Typography variant="body2">-40°C to +85°C</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" fontWeight={600}>Power Consumption:</Typography>
                  <Typography variant="body2">Ultra-low 50µA</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" fontWeight={600}>Battery Life:</Typography>
                  <Typography variant="body2">5+ years</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" fontWeight={600}>Communication Range:</Typography>
                  <Typography variant="body2">Up to 15km</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" fontWeight={600}>IP Rating:</Typography>
                  <Chip label="IP68" size="small" color="primary" />
                </Box>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#FF8C00' }}>
                Software & Platform
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" fontWeight={600}>Frontend:</Typography>
                  <Typography variant="body2">React 19 + TypeScript</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" fontWeight={600}>UI Framework:</Typography>
                  <Typography variant="body2">Material-UI v5</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" fontWeight={600}>Real-time Updates:</Typography>
                  <Typography variant="body2">WebSocket + MQTT</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" fontWeight={600}>Data Visualization:</Typography>
                  <Typography variant="body2">Recharts + D3.js</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" fontWeight={600}>Update Frequency:</Typography>
                  <Chip label="Real-time" size="small" color="success" />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" fontWeight={600}>Data Retention:</Typography>
                  <Typography variant="body2">5 years</Typography>
                </Box>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 3, background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                Advanced Analytics & AI Features
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Box>
                    <Typography variant="body1" fontWeight={600} sx={{ mb: 1 }}>
                      Machine Learning
                    </Typography>
                    <Typography variant="body2">
                      • Predictive maintenance algorithms
                      <br />• Anomaly detection models
                      <br />• Consumption pattern analysis
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box>
                    <Typography variant="body1" fontWeight={600} sx={{ mb: 1 }}>
                      Data Processing
                    </Typography>
                    <Typography variant="body2">
                      • Real-time stream processing
                      <br />• Edge computing capabilities
                      <br />• Cloud-based data analytics
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box>
                    <Typography variant="body1" fontWeight={600} sx={{ mb: 1 }}>
                      Security Features
                    </Typography>
                    <Typography variant="body2">
                      • End-to-end encryption
                      <br />• Multi-factor authentication
                      <br />• Role-based access control
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>
      ),
    },
    {
      id: 11,
      title: 'Technology Stack & Integration',
      background: 'linear-gradient(135deg, #f9fafb 0%, #f0f5fa 100%)',
      content: (
        <Box sx={{ py: 2 }}>
          <Typography variant="h6" sx={{ mb: 4, color: '#1a365d', textAlign: 'center', fontWeight: 700 }}>
            Enterprise-Grade Technology Stack for Maximum Performance & Reliability
          </Typography>

          {/* Top Row - Main Technology Categories */}
          <Grid container spacing={4} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 4, background: 'linear-gradient(45deg, #00A651 0%, #00d4aa 100%)', color: 'white', height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Dashboard sx={{ fontSize: 40, mr: 2 }} />
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>
                    Frontend & User Interface
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" fontWeight={700}>Framework:</Typography>
                    <Typography variant="body1" fontWeight={600}>React 19 + TypeScript</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" fontWeight={700}>UI Library:</Typography>
                    <Typography variant="body1" fontWeight={600}>Material-UI v5</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" fontWeight={700}>Visualization:</Typography>
                    <Typography variant="body1" fontWeight={600}>Recharts + D3.js</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" fontWeight={700}>PWA Support:</Typography>
                    <Typography variant="body1" fontWeight={600}>Offline Capabilities</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip label="React 19" sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 'bold' }} />
                  <Chip label="TypeScript" sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 'bold' }} />
                  <Chip label="Material-UI" sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 'bold' }} />
                  <Chip label="PWA" sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 'bold' }} />
                  <Chip label="Responsive" sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 'bold' }} />
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ p: 4, background: 'linear-gradient(45deg, #FF8C00 0%, #ffa726 100%)', color: 'white', height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Settings sx={{ fontSize: 40, mr: 2 }} />
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>
                    Backend & IoT Infrastructure
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" fontWeight={700}>Runtime:</Typography>
                    <Typography variant="body1" fontWeight={600}>Node.js + Express</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" fontWeight={700}>IoT Protocol:</Typography>
                    <Typography variant="body1" fontWeight={600}>MQTT + LoRaWAN</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" fontWeight={700}>Communication:</Typography>
                    <Typography variant="body1" fontWeight={600}>Modbus RTU/TCP</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" fontWeight={700}>API:</Typography>
                    <Typography variant="body1" fontWeight={600}>REST + GraphQL</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip label="Node.js" sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 'bold' }} />
                  <Chip label="MQTT" sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 'bold' }} />
                  <Chip label="LoRaWAN" sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 'bold' }} />
                  <Chip label="Modbus" sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 'bold' }} />
                  <Chip label="Real-time" sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 'bold' }} />
                </Box>
              </Card>
            </Grid>
          </Grid>

          {/* Second Row - Data & Security */}
          <Grid container spacing={4} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 4, background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)', color: 'white', height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Analytics sx={{ fontSize: 40, mr: 2 }} />
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>
                    Database & Storage Layer
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" fontWeight={700}>Document DB:</Typography>
                    <Typography variant="body1" fontWeight={600}>MongoDB Atlas</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" fontWeight={700}>Time Series:</Typography>
                    <Typography variant="body1" fontWeight={600}>InfluxDB</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" fontWeight={700}>Cache:</Typography>
                    <Typography variant="body1" fontWeight={600}>Redis Cluster</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" fontWeight={700}>Storage:</Typography>
                    <Typography variant="body1" fontWeight={600}>AWS S3 + CloudFront</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip label="MongoDB" sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 'bold' }} />
                  <Chip label="InfluxDB" sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 'bold' }} />
                  <Chip label="Redis" sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 'bold' }} />
                  <Chip label="AWS S3" sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 'bold' }} />
                  <Chip label="5yr Retention" sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 'bold' }} />
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ p: 4, background: 'linear-gradient(45deg, #f093fb 0%, #f5576c 100%)', color: 'white', height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Shield sx={{ fontSize: 40, mr: 2 }} />
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>
                    DevOps & Security Framework
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" fontWeight={700}>Containers:</Typography>
                    <Typography variant="body1" fontWeight={600}>Docker + Kubernetes</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" fontWeight={700}>Security:</Typography>
                    <Typography variant="body1" fontWeight={600}>SSL/TLS + AES-256</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" fontWeight={700}>Auth:</Typography>
                    <Typography variant="body1" fontWeight={600}>JWT + OAuth 2.0</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" fontWeight={700}>CI/CD:</Typography>
                    <Typography variant="body1" fontWeight={600}>GitHub Actions</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip label="Docker" sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 'bold' }} />
                  <Chip label="Kubernetes" sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 'bold' }} />
                  <Chip label="SSL/TLS" sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 'bold' }} />
                  <Chip label="JWT" sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 'bold' }} />
                  <Chip label="CI/CD" sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 'bold' }} />
                </Box>
              </Card>
            </Grid>
          </Grid>

          {/* Third Row - Integration Capabilities */}
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Card sx={{ p: 4, background: 'linear-gradient(45deg, #4ecdc4 0%, #2ecc71 100%)', color: 'white' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, textAlign: 'center' }}>
                  Enterprise Integration & API Capabilities
                </Typography>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h3" sx={{ fontWeight: 800, color: '#FFD700', mb: 1 }}>REST</Typography>
                      <Typography variant="h6" fontWeight={700}>API Standards</Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        RESTful APIs with comprehensive documentation for seamless integration
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h3" sx={{ fontWeight: 800, color: '#FFD700', mb: 1 }}>99.9%</Typography>
                      <Typography variant="h6" fontWeight={700}>API Uptime</Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Enterprise-grade reliability with comprehensive monitoring and alerting
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h3" sx={{ fontWeight: 800, color: '#FFD700', mb: 1 }}>50+</Typography>
                      <Typography variant="h6" fontWeight={700}>API Endpoints</Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Comprehensive API coverage for all system functions and data access
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h3" sx={{ fontWeight: 800, color: '#FFD700', mb: 1 }}>24/7</Typography>
                      <Typography variant="h6" fontWeight={700}>Support</Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Round-the-clock technical support for integration and troubleshooting
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          </Grid>
        </Box>
      ),
    },
    {
      id: 12,
      title: 'Implementation Roadmap',
      subtitle: '3-Phase Deployment Strategy for Success',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      content: (
        <Grid container spacing={4} sx={{ py: 2 }}>
          <Grid item xs={12} md={4}>
            <Card sx={{ p: 4, height: '100%', background: 'linear-gradient(45deg, #00A651 0%, #00d4aa 100%)', color: 'white' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <CheckCircle sx={{ fontSize: 32 }} />
                Phase 1: Foundation
              </Typography>
              <Typography variant="h6" sx={{ mb: 2, color: '#FFD700' }}>Weeks 1-4</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="body1" fontWeight={600}>Infrastructure Setup</Typography>
                  <Typography variant="body2">Server deployment and configuration</Typography>
                </Box>
                <Box>
                  <Typography variant="body1" fontWeight={600}>Basic Monitoring</Typography>
                  <Typography variant="body2">Core fuel level tracking deployment</Typography>
                </Box>
                <Box>
                  <Typography variant="body1" fontWeight={600}>User Training</Typography>
                  <Typography variant="body2">Comprehensive staff onboarding</Typography>
                </Box>
                <Box>
                  <Typography variant="body1" fontWeight={600}>System Testing</Typography>
                  <Typography variant="body2">Initial validation and configuration</Typography>
                </Box>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ p: 4, height: '100%', background: 'linear-gradient(45deg, #FF8C00 0%, #ffa726 100%)', color: 'white' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Settings sx={{ fontSize: 32 }} />
                Phase 2: Enhancement
              </Typography>
              <Typography variant="h6" sx={{ mb: 2, color: '#FFD700' }}>Weeks 5-8</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="body1" fontWeight={600}>Advanced Analytics</Typography>
                  <Typography variant="body2">Predictive insights and reporting</Typography>
                </Box>
                <Box>
                  <Typography variant="body1" fontWeight={600}>IoT Device Deployment</Typography>
                  <Typography variant="body2">Sensor installation and configuration</Typography>
                </Box>
                <Box>
                  <Typography variant="body1" fontWeight={600}>Mobile Application</Typography>
                  <Typography variant="body2">Field operations capability rollout</Typography>
                </Box>
                <Box>
                  <Typography variant="body1" fontWeight={600}>Security Features</Typography>
                  <Typography variant="body2">Theft detection and alert systems</Typography>
                </Box>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ p: 4, height: '100%', background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <TrendingUp sx={{ fontSize: 32 }} />
                Phase 3: Optimization
              </Typography>
              <Typography variant="h6" sx={{ mb: 2, color: '#FFD700' }}>Weeks 9-12</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="body1" fontWeight={600}>Performance Tuning</Typography>
                  <Typography variant="body2">System optimization and fine-tuning</Typography>
                </Box>
                <Box>
                  <Typography variant="body1" fontWeight={600}>Advanced Reporting</Typography>
                  <Typography variant="body2">Custom dashboards and analytics</Typography>
                </Box>
                <Box>
                  <Typography variant="body1" fontWeight={600}>Integration Testing</Typography>
                  <Typography variant="body2">Full system validation and testing</Typography>
                </Box>
                <Box>
                  <Typography variant="body1" fontWeight={600}>Go-Live Support</Typography>
                  <Typography variant="body2">24/7 monitoring and support</Typography>
                </Box>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 3, background: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(10px)' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#00A651', textAlign: 'center' }}>
                Project Timeline & Deliverables
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#00A651' }}>12</Typography>
                    <Typography variant="body2" sx={{ color: '#1a365d', fontWeight: 600 }}>Weeks Implementation</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#FF8C00' }}>24/7</Typography>
                    <Typography variant="body2" sx={{ color: '#1a365d', fontWeight: 600 }}>Post-launch Support</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#667eea' }}>30</Typography>
                    <Typography variant="body2" sx={{ color: '#1a365d', fontWeight: 600 }}>Day Risk-free Trial</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#4caf50' }}>ROI</Typography>
                    <Typography variant="body2" sx={{ color: '#1a365d', fontWeight: 600 }}>6-12 Month Payback</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>
      ),
    },
    {
      id: 13,
      title: 'Support & Maintenance Services',
      subtitle: 'Ensuring Long-term Success and System Reliability',
      background: '#ffffff',
      content: (
        <Grid container spacing={4} sx={{ py: 2 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 4, height: '100%' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#00A651' }}>
                🔧 Technical Support Packages
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Card sx={{ p: 3, background: 'linear-gradient(45deg, #00A651 20%, #00d4aa 100%)', color: 'white' }}>
                  <Typography variant="h6" fontWeight={700}>Premium Support</Typography>
                  <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="body2">• 24/7 system monitoring</Typography>
                    <Typography variant="body2">• 2-hour response time</Typography>
                    <Typography variant="body2">• Dedicated support engineer</Typography>
                    <Typography variant="body2">• Proactive maintenance</Typography>
                  </Box>
                </Card>
                <Card sx={{ p: 3, background: 'linear-gradient(45deg, #FF8C00 20%, #ffa726 100%)', color: 'white' }}>
                  <Typography variant="h6" fontWeight={700}>Standard Support</Typography>
                  <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="body2">• Business hours monitoring</Typography>
                    <Typography variant="body2">• 4-hour response time</Typography>
                    <Typography variant="body2">• Email and phone support</Typography>
                    <Typography variant="body2">• Monthly health checks</Typography>
                  </Box>
                </Card>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 4, height: '100%' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#FF8C00' }}>
                📚 Training & Documentation
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                  <CheckCircle sx={{ color: '#4caf50', fontSize: 24, mt: 0.5 }} />
                  <Box>
                    <Typography variant="body1" fontWeight={600}>Comprehensive User Manuals</Typography>
                    <Typography variant="body2" sx={{ color: '#1a365d', fontWeight: 600 }}>Step-by-step guides for all user roles</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                  <CheckCircle sx={{ color: '#4caf50', fontSize: 24, mt: 0.5 }} />
                  <Box>
                    <Typography variant="body1" fontWeight={600}>Video Training Library</Typography>
                    <Typography variant="body2" sx={{ color: '#1a365d', fontWeight: 600 }}>Interactive tutorials and walkthroughs</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                  <CheckCircle sx={{ color: '#4caf50', fontSize: 24, mt: 0.5 }} />
                  <Box>
                    <Typography variant="body1" fontWeight={600}>Regular Refresher Sessions</Typography>
                    <Typography variant="body2" sx={{ color: '#1a365d', fontWeight: 600 }}>Quarterly training updates</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                  <CheckCircle sx={{ color: '#4caf50', fontSize: 24, mt: 0.5 }} />
                  <Box>
                    <Typography variant="body1" fontWeight={600}>24/7 Knowledge Base</Typography>
                    <Typography variant="body2" sx={{ color: '#1a365d', fontWeight: 600 }}>Searchable documentation and FAQs</Typography>
                  </Box>
                </Box>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 4, background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, textAlign: 'center' }}>
                Service Level Agreement (SLA) Commitments
              </Typography>
              <Grid container spacing={4}>
                <Grid item xs={12} md={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 800, color: '#FFD700' }}>99.9%</Typography>
                    <Typography variant="h6" fontWeight={600}>System Uptime</Typography>
                    <Typography variant="body2">Guaranteed availability</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 800, color: '#FFD700' }}>2Hr</Typography>
                    <Typography variant="h6" fontWeight={600}>Critical Response</Typography>
                    <Typography variant="body2">Maximum response time</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 800, color: '#FFD700' }}>Monthly</Typography>
                    <Typography variant="h6" fontWeight={600}>Health Reviews</Typography>
                    <Typography variant="body2">Performance analysis</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 800, color: '#FFD700' }}>Free</Typography>
                    <Typography variant="h6" fontWeight={600}>Updates</Typography>
                    <Typography variant="body2">Feature enhancements</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>
      ),
    },
    {
      id: 14,
      title: 'Thank You',
      subtitle: 'Questions & Discussion',
      content: (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Box sx={{ mb: 4 }}>
            <LocalGasStation
              sx={{
                fontSize: 100,
                color: '#00A651',
                filter: 'drop-shadow(0 4px 8px rgba(0, 166, 81, 0.3))',
              }}
            />
          </Box>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, color: '#00A651' }}>
            Thank You!
          </Typography>
          <Typography variant="h6" sx={{ color: '#1a365d', mb: 4, maxWidth: 600, mx: 'auto', fontWeight: 600 }}>
            Ready to revolutionize your fuel management operations?
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              sx={{
                background: 'linear-gradient(45deg, #00A651 0%, #FF8C00 100%)',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
              }}
            >
              Get Started
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{
                borderColor: '#00A651',
                color: '#00A651',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                '&:hover': {
                  borderColor: '#00A651',
                  backgroundColor: 'rgba(0, 166, 81, 0.04)',
                }
              }}
            >
              Learn More
            </Button>
          </Box>
          <Typography variant="body1" sx={{ mt: 4, color: '#1a365d', fontWeight: 600 }}>
            Contact: info@fuelmonitor.com | +1 (555) 123-4567
          </Typography>
        </Box>
      ),
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    },
  ];

  useEffect(() => {
    if (isPlaying) {
      const interval = setInterval(() => {
        nextSlide();
      }, 10000); // Auto advance every 10 seconds
      return () => clearInterval(interval);
    }
  }, [isPlaying, currentSlide]);

  const nextSlide = () => {
    setSlideDirection('right');
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setSlideDirection('left');
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index: number) => {
    setSlideDirection(index > currentSlide ? 'right' : 'left');
    setCurrentSlide(index);
  };

  const togglePlayback = () => {
    setIsPlaying(!isPlaying);
  };

  const currentSlideData = slides[currentSlide];

  return (
    <FuelMonitoringThemeProvider>
      <Box
        sx={{
          minHeight: '100vh',
          background: currentSlideData.background || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          transition: 'background 0.5s ease',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Navigation Header */}
        <Paper
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <LocalGasStation sx={{ color: '#00A651', fontSize: 32 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#00A651' }}>
                Fuel Monitor
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton onClick={prevSlide} disabled={currentSlide === 0}>
                <NavigateBefore />
              </IconButton>

              <IconButton onClick={togglePlayback}>
                {isPlaying ? <Pause /> : <PlayArrow />}
              </IconButton>

              <IconButton onClick={nextSlide} disabled={currentSlide === slides.length - 1}>
                <NavigateNext />
              </IconButton>

              <Typography variant="body2" sx={{ mx: 2, color: '#1a365d', fontWeight: 600 }}>
                {currentSlide + 1} / {slides.length}
              </Typography>
            </Box>
          </Box>

          {/* Progress Bar */}
          <LinearProgress
            variant="determinate"
            value={(currentSlide + 1) / slides.length * 100}
            sx={{
              height: 3,
              '& .MuiLinearProgress-bar': {
                background: 'linear-gradient(45deg, #00A651 0%, #FF8C00 100%)',
              },
            }}
          />
        </Paper>

        {/* Slide Content */}
        <Container maxWidth="xl" sx={{ pt: 10, pb: 4 }}>
          <Slide
            direction={slideDirection}
            in={true}
            timeout={500}
            key={currentSlide}
          >
            <Box>
              <Fade in timeout={1000}>
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 800,
                      color: '#1a365d',
                      textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                      mb: 1,
                      fontSize: { xs: '2rem', md: '3rem' },
                      ...(currentSlideData.background === '#ffffff' ||
                          currentSlideData.background?.includes('f5f7fa') ||
                          currentSlideData.background?.includes('fafbfc') ||
                          currentSlideData.background?.includes('f8fafb') ||
                          currentSlideData.background?.includes('f9fafb') ||
                          currentSlideData.background?.includes('c3cfe2')) && {
                        color: '#1a365d',
                        fontWeight: 900
                      }
                    }}
                  >
                    {currentSlideData.title}
                  </Typography>
                  {currentSlideData.subtitle && (
                    <Typography
                      variant="h5"
                      sx={{
                        color: '#1a365d',
                        fontWeight: 600,
                        fontSize: { xs: '1.2rem', md: '1.5rem' },
                        textShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                        ...(currentSlideData.background === '#ffffff' ||
                            currentSlideData.background?.includes('f5f7fa') ||
                            currentSlideData.background?.includes('fafbfc') ||
                            currentSlideData.background?.includes('f8fafb') ||
                            currentSlideData.background?.includes('f9fafb') ||
                            currentSlideData.background?.includes('c3cfe2')) && {
                          color: '#1a365d',
                          fontWeight: 600
                        }
                      }}
                    >
                      {currentSlideData.subtitle}
                    </Typography>
                  )}
                </Box>
              </Fade>

              <Fade in timeout={1500}>
                <Box>
                  {currentSlideData.content}
                </Box>
              </Fade>
            </Box>
          </Slide>
        </Container>

        {/* Slide Indicators */}
        <Box
          sx={{
            position: 'fixed',
            bottom: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 1,
            zIndex: 1000,
          }}
        >
          {slides.map((_, index) => (
            <IconButton
              key={index}
              size="small"
              onClick={() => goToSlide(index)}
              sx={{
                width: 12,
                height: 12,
                backgroundColor: index === currentSlide ? '#00A651' : 'rgba(255, 255, 255, 0.5)',
                '&:hover': {
                  backgroundColor: index === currentSlide ? '#00A651' : 'rgba(255, 255, 255, 0.8)',
                },
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Global Styles */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }

          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>
    </FuelMonitoringThemeProvider>
  );
};

export default Presentation;